import { useCallback, useRef } from 'react';
import { useHover } from '@/features/hoverContext';
import { OVERVIEW_ZOOM_THRESHOLD } from '@/app/constants';
import { LayerType } from './layers/types';

export const GROUP_HOVER_DELAY_MS = 200;

interface UseMapGroupHoverOptions {
  viewStateRef: React.RefObject<{ zoom: number }>;
  isSpanView: boolean;
}

/** The deck.gl `PickingInfo` fields that group hover reads. */
export interface GroupHoverInfo {
  object?: { properties?: { group?: string } | null };
  layer?: { id: string } | null;
  coordinate?: number[];
}

export const useMapGroupHover = ({
  viewStateRef,
  isSpanView,
}: UseMapGroupHoverOptions) => {
  const { setHoveredItem } = useHover();
  const lastHoveredGroupRef = useRef<string | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  return useCallback(
    (d: GroupHoverInfo | null) => {
      const isOverviewZoomLevel =
        viewStateRef.current.zoom <= OVERVIEW_ZOOM_THRESHOLD;
      const isScanLayer =
        d?.object?.properties && d.layer?.id === LayerType[LayerType.scan];
      const group = d?.object?.properties?.group;
      const coordinate = d?.coordinate;

      if (
        isSpanView ||
        !isOverviewZoomLevel ||
        !isScanLayer ||
        !group ||
        !coordinate
      ) {
        // Clear any pending hover timeout
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        lastHoveredGroupRef.current = null;
        setHoveredItem(null);
        return;
      }

      if (lastHoveredGroupRef.current === group) return;

      // Clear any pending hover timeout before setting a new one
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);

      // Display a group's name in a tooltip upon hovering over the group, but only after a short delay to prevent flickering
      hoverTimeoutRef.current = setTimeout(() => {
        lastHoveredGroupRef.current = group;
        setHoveredItem({
          source: 'map',
          groupName: group,
          coordinate: { lng: coordinate[0], lat: coordinate[1] },
        });
      }, GROUP_HOVER_DELAY_MS);
    },
    [isSpanView, setHoveredItem, viewStateRef]
  );
};
